<script>
import { picoSearch } from '@scmmishra/pico-search';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';
import LabelDropdownItem from './LabelDropdownItem.vue';

export default {
  components: {
    LabelDropdownItem,
    AddLabelModal,
    Hotkey,
  },

  props: {
    accountLabels: {
      type: Array,
      default: () => [],
    },
    selectedLabels: {
      type: Array,
      default: () => [],
    },
    allowCreation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      search: '',
      createModalVisible: false,
      pendingCreatedLabel: null,
      pendingCreatedSearch: '',
    };
  },

  computed: {
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },

    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;

      return picoSearch(this.accountLabels, this.search, ['title'], {
        threshold: 0.9,
      });
    },

    noResult() {
      return this.filteredActiveLabels.length === 0;
    },

    hasExactMatchInResults() {
      if (!this.parsedSearch) return false;

      return this.accountLabels.some(
        label => sanitizeLabel(label.title) === this.parsedSearch
      );
    },

    shouldShowCreate() {
      return this.allowCreation && !this.hasExactMatchInResults;
    },

    parsedSearch() {
      return sanitizeLabel(this.search);
    },
  },

  watch: {
    accountLabels: {
      handler() {
        this.reconcilePendingCreatedLabel();
      },
      deep: true,
    },
  },

  mounted() {
    this.focusInput();
  },

  beforeDestroy() {
    if (this.createModalVisible) {
      this.$emit('toggle-create-modal', false);
    }
  },

  methods: {
    focusInput() {
      this.$refs.searchbar.focus();
    },

    updateLabels(label) {
      this.$emit('update', label);
    },

    onAdd(label) {
      this.$emit('add', label);
    },

    onRemove(label) {
      this.$emit('remove', label);
    },

    onAddRemove(label) {
      if (this.selectedLabels.includes(label.title)) {
        this.onRemove(label.title);
      } else {
        this.onAdd(label);
      }
    },

    showCreateModal() {
      this.pendingCreatedSearch = this.parsedSearch;
      this.createModalVisible = true;
      this.$emit('toggle-create-modal', true);
    },

    handleCreatedLabel(label) {
      this.pendingCreatedLabel = label?.payload || label;
      this.reconcilePendingCreatedLabel({
        force: true,
      });
    },

    hideCreateModal() {
      this.createModalVisible = false;
      this.$emit('toggle-create-modal', false);
      this.reconcilePendingCreatedLabel();
    },

    reconcilePendingCreatedLabel({ force = false } = {}) {
      if (this.createModalVisible && !force) return;

      const createdLabel = this.pendingCreatedLabel?.title
        ? this.pendingCreatedLabel
        : this.accountLabels.find(
            item => sanitizeLabel(item.title) === this.pendingCreatedSearch
          );

      if (!createdLabel?.title) return;

      if (
        createdLabel?.title &&
        !this.selectedLabels.includes(createdLabel.title)
      ) {
        this.onAdd(createdLabel);
      }

      this.search = '';
      this.pendingCreatedLabel = null;
      this.pendingCreatedSearch = '';
      this.createModalVisible = false;
      this.$emit('toggle-create-modal', false);
      this.$emit('close-dropdown');
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-h-[12.5rem]">
    <div class="flex items-center justify-center mb-1">
      <h4
        class="flex-grow m-0 overflow-hidden text-sm text-body whitespace-nowrap text-ellipsis"
      >
        {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.TITLE') }}
      </h4>
      <Hotkey
        custom-class="border border-solid text-body bg-surface-hover text-xxs border-line-faint"
      >
        {{ 'L' }}
      </Hotkey>
    </div>
    <div class="flex-auto flex-grow-0 flex-shrink-0 mb-2 max-h-8">
      <input
        ref="searchbar"
        v-model="search"
        type="text"
        class="search-input"
        autofocus="true"
        :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
      />
    </div>
    <div
      class="flex items-start justify-start flex-auto flex-grow flex-shrink overflow-auto"
    >
      <div class="w-full">
        <div
          v-if="allowCreation && shouldShowCreate"
          class="flex pb-1 mb-1 border-b border-solid border-line-faint"
        >
          <woot-button
            size="small"
            variant="clear"
            color-scheme="secondary"
            icon="add"
            is-expanded
            class="button-new-label"
            :is-disabled="hasExactMatchInResults"
            @click="showCreateModal"
          >
            {{ createLabelPlaceholder }}
            {{ parsedSearch }}
          </woot-button>

          <woot-modal
            :show.sync="createModalVisible"
            :on-close="hideCreateModal"
          >
            <AddLabelModal
              :prefill-title="parsedSearch"
              :on-created="handleCreatedLabel"
              :on-close="hideCreateModal"
              @label-created="handleCreatedLabel"
              @created="handleCreatedLabel"
            />
          </woot-modal>
        </div>
        <woot-dropdown-menu>
          <LabelDropdownItem
            v-for="label in filteredActiveLabels"
            :key="label.title"
            :title="label.title"
            :color="label.color"
            :selected="selectedLabels.includes(label.title)"
            @click="onAddRemove(label)"
          />
        </woot-dropdown-menu>
        <div
          v-if="noResult"
          class="flex justify-center py-4 px-2.5 font-medium text-xs text-muted"
        >
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hotkey {
  @apply flex-shrink-0;
}

.search-input {
  @apply m-0 w-full border border-solid border-transparent h-8 text-sm text-muted rounded-md focus:border-woot-500 bg-surface-sunken;
}

.button-new-label {
  @apply whitespace-nowrap text-ellipsis overflow-hidden items-center;

  .icon {
    @apply min-w-0;
  }
}
</style>
